import useGeolocation from '@website/hooks/geoLocation/useGeolocation';
import parsePhoneNumber from 'libphonenumber-js';
import { InputHTMLAttributes } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, UseFormReturn } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';

/**
 * Phone Number Input Props Type
 *
 * Note we specify the particular HTML attributes that may be added, as most don't apply with `react-phone-input-2` (the
 * underlying component used).
 */
type PhoneNoInputProps = Pick<
  InputHTMLAttributes<any>,
  'autoFocus' | 'disabled' | 'required' | 'id' | 'role'
> & {
  name: string;
  control: UseFormReturn<any>['control'];
  setError: UseFormReturn<any>['setError'];
  clearErrors: UseFormReturn<any>['clearErrors'];
};

export function isCountryCodeOnly(phone: string): boolean {
  const isCountryCode = phone.match(/^(\+?\d{1,3}|\d{1,4})$/);
  return !!isCountryCode;
}

export function validatePhoneNumber(
  value: string,
  required: boolean,
): boolean | any {
  // Allow if not required and not set
  if (!required && !value) return true;

  // Allow if not require and only the country code is set
  if (!required && isCountryCodeOnly(value)) return true;

  // Get the formatted phone number
  const valueWithPlus = value.startsWith('+') ? value : `+${value}`;
  const phoneNumber = parsePhoneNumber(valueWithPlus);

  // Validate
  if (!phoneNumber || !phoneNumber?.isValid()) {
    return false;
  }
  return true;
}

/**
 * PhoneNoInput Field
 *
 * A bootstrap phone number field for use with react-hook-form. It is a wrapper
 * for react-phone-input-2 and has a countries drop down to allow the users to
 * select their country of choice before adding a phone no. It also defaults to
 * the current geolocation of the user.
 *
 * __Important:__ if using this field, make sure to set `shouldFocusError` on
 *  `useForm` as this field uses non-native validation.
 */
export default function PhoneNoInput({
  autoFocus = false,
  control,
  disabled = false,
  id,
  name,
  required = false,
  role,
  setError,
  clearErrors,
}: PhoneNoInputProps) {
  const geo = useGeolocation();

  const defaultCountry = (geo?.country?.code as string) || 'GB';
  const defaultCountryCode = geo?.phoneCode ? `+${geo.phoneCode}` : '+44';

  return (
    <>
      <Controller
        render={({ field, fieldState: { error } }) => (
          <div>
            <PhoneInput
              value={field.value}
              onChange={(value) => {
                const valid = validatePhoneNumber(value, required);
                if (!valid) {
                  setError(name, { message: 'Phone number is not valid!' });
                  return;
                }

                clearErrors(name);
                // Get E164 form Phone Number Number
                const phoneNumber = parsePhoneNumber(`+${value}`);
                field.onChange(phoneNumber?.number);
              }}
              onBlur={field.onBlur}
              country={defaultCountry.toLowerCase()}
              countryCodeEditable={false}
              enableSearch
              autoFormat={false}
              inputClass="w-100 py-2"
              prefix="+"
              enableTerritories
              // exclude all territories except the gibralter
              excludeCountries={['je', 'gu', 'im', 'gg', 'pr', 'as', 'vi']}
              enableAreaCodeStretch
              inputProps={{
                required,
                autoFocus,
                disabled,
                id,
                name: field.name,
                role,
                ref: field.ref,
                // Add red border if invalid
                style: { borderColor: error ? '#dc3545' : undefined },
              }}
            />
            {error && (
              <Form.Text as="p" className="text-danger">
                {error?.message || 'Phone number is not valid!'}
              </Form.Text>
            )}
          </div>
        )}
        name={name}
        control={control}
        defaultValue={defaultCountryCode}
        rules={{ required, validate: (v) => validatePhoneNumber(v, required) }}
      />
    </>
  );
}
